import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnInit } from '@angular/core';
import { HomeTabs, Search } from '@local/client-contracts';
import { performanceCheckpoint } from '@local/common';
import { PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { DateFormat } from '@shared/consts';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import * as moment from 'moment';
import { WorkspacesService } from 'src/app/bar/services';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SearchOptions, SearchService } from 'src/app/bar/services/search';
import { LinkResourcesSourceSettings } from 'src/app/bar/services/search/client';
import { ScrollTrigger, SearchResults } from '../../../results';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { WidgetsService } from '../../services/widgets.service';
import { WidgetBaseComponent } from '../widget-base.component';

type ShowResultSections = {
  showBullets: boolean;
  showContextMenu: boolean;
  showTags: boolean;
  enableCommandBar: boolean;
  showSnippet: boolean;
  showSubtitleInSecondLine: boolean;
};

@UntilDestroy()
@Component({
  selector: 'shared-with-me',
  templateUrl: './shared-with-me.component.html',
  styleUrls: ['./shared-with-me.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedWithMeComponent extends WidgetBaseComponent<HomeTabs.Widget> implements OnInit {
  displayedItems: (Search.ResultItem & { showResultSections: ShowResultSections })[] = [];
  lastPageInfo: { startIndex: number; endIndex: number; last: boolean };

  readonly DEFAULT_RESULTS_SECTIONS = {
    showBullets: false,
    showTags: false,
    showSnippet: false,
    showSubView: false,
    showSubtitleInSecondLine: true,
    showPreviewIcon: false,
  };

  constructor(
    protected elementRef: ElementRef,
    protected eventsService: EventsService,
    protected hubService: HubService,
    protected searchService: SearchService,
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    protected ngZone: NgZone,
    protected session: SessionService,
    protected resultCommandService: ResultCommandService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected popupService: PopupService,
    protected routerService: RouterService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService,
  ) {
    super(
      elementRef,
      cdr,
      keyboardService,
      widgetsService,
      ngZone,
      session,
      contextMenuService,
      workspacesService,
      popupService,
      eventsService,
      routerService,
      homeTabsService,
      avatarListService
    );
    this.searchSession = this.searchService.getOrCreateSearchSession('sharedWithMeWidget');
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.session.current$.pipe(untilDestroyed(this)).subscribe((c) => {
      this.currentSession = c;
    });
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.scrollService.scroll$.pipe(untilDestroyed(this)).subscribe(({ trigger }) => {
      this.prepareNextPage(trigger);
    });
  }

  async loadData() {
    this.isLoading = true;
    const options: SearchOptions = {
      resetSession: true,
      trigger: 'shared_with_widget_load',
      sources: [
        {
          type: 'link-resources',
          requestMaxCount: 20,
          useSourceFilters: true,
          caching: { strategy: 'cache-and-source' },
          noHeader: true,
          filters: { preFilters: { sharedWith: [this.currentSession?.user?.email, 'me'] } },
          sorting: {
            by: 'Timestamp',
            order: 'Descending',
            type: 'Time',
          },
          tag: 'shared-with-me-widget',
        } as LinkResourcesSourceSettings,
      ],
    };
    performanceCheckpoint('time-to-shared');
    this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe((ctx) => {
        performanceCheckpoint('time-to-shared-ping');
        const resourceSource = this.displayedContext.sources?.find((s) => s.source.type === 'link-resources');

        let items = ctx.items.filter((elm) => elm.type !== 'header');
        this.displayedItems = this.setBullets(items);
        ctx.items = items;
        this.displayedContext = { ...ctx };

        this.lastPageInfo = {
          endIndex: this.displayedItems.length - 1,
          startIndex: resourceSource?.extra?.lastPageInfo?.startIndex,
          last: true,
        };
        this.isLoading = !ctx.items.length && !ctx.searchCompleted;
        this.cdr.detectChanges();
      });
  }

  private setBullets(items: SearchResults[]): any {
    return items.map((item: Search.ResultResourceItem) => {
      const showResultSections = this.DEFAULT_RESULTS_SECTIONS;
      const sharedByName =
        item.resource?.traits?.sharedBy?.name || item.resource?.traits?.sharedBy?.username || item.resource?.traits?.sharedBy?.email;
      const timeAgo = moment(item.resource?.traits?.sharedAt).fromNow();

      if (sharedByName && timeAgo) {
        item.view.bullets = [
          {
            parts: [
              {
                text: `Shared by ${sharedByName} ${timeAgo}`,
                icon: { name: 'icon-user', lightUrl: 'icon-user' },
                tooltip: moment(item.resource?.traits?.sharedAt).format(DateFormat.FULL_DATE_HOURS_A),
              },
            ],
          },
        ];
        showResultSections.showBullets = true;
      }
      return { ...item, showResultSections };
    });
  }

  public async onInvoke(data: InvokeCommand, type: Search.ResultType): Promise<void> {
    this.resultCommandService.onInvoke(
      this.displayedContext.items,
      data,
      type,
      'sharedWithMe',
      'sharedWithMe',
      'sharedWithMe',
      this.selectedIndex,
      0,
      undefined,
      undefined,
      'shared_with_me'
    );
  }

  async prepareNextPage(trigger: ScrollTrigger) {
    const last = this.scrollService.resultInView('last');
    if (!last || !this.displayedContext.searchCompleted) return;
    if (parseInt(last.getAttribute('data-index')) < this.lastPageInfo?.endIndex) {
      return;
    }
    const source = this.displayedContext.sources[0]?.source;
    this.searchSession.nextPage$(trigger, [({ id: source?.id, type: source?.type })]).subscribe((ctx) => {
      if (!ctx) {
        return;
      }
    });
  }
}
